import { Button } from "@/components/ui/button";
import { Phone, ArrowRight, Star, Check, ShieldCheck, CheckCircle2 } from "lucide-react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { COMPANY, SITE_IMAGES } from "@/lib/site-data";

export const Hero = () => {
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative min-h-[100vh] w-full flex items-center overflow-hidden"
    >
      {/* Background image with subtle ken-burns */}
      <motion.div
        initial={{ scale: 1.08 }}
        animate={{ scale: 1 }}
        transition={{ duration: 8, ease: "easeOut" }}
        className="absolute inset-0"
      >
        <img
          src={SITE_IMAGES.hero}
          alt="Egyedi teherautó ponyva – Fortuna Ponyva"
          className="w-full h-full object-cover"
          style={{ filter: "brightness(1.12) saturate(1.05)" }}
        />
        <div className="absolute inset-0 hero-overlay" />
      </motion.div>

      {/* Floating "Ingyenes ajánlatkérés" pill */}
      <Link
        to="/#ajanlat"
        className="hidden lg:flex absolute top-28 right-8 z-20 items-center gap-2.5 bg-[#EA580C] hover:bg-[#C2410C] text-white pl-3 pr-5 py-2.5 rounded-full shadow-2xl shadow-orange-900/40 float-anim group transition-colors"
      >
        <span className="w-7 h-7 rounded-full bg-white/20 flex items-center justify-center">
          <CheckCircle2 className="w-4 h-4 text-white" />
        </span>
        <span className="font-heading font-bold tracking-[0.18em] text-xs uppercase">
          Ingyenes ajánlatkérés
        </span>
        <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" />
      </Link>

      <div className="relative max-w-7xl mx-auto px-4 md:px-8 pt-28 pb-20 md:pt-36 md:pb-28 w-full">
        <div className="max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur border border-white/20 text-white text-xs font-semibold tracking-widest uppercase"
          >
            <span className="relative flex">
              <span className="w-1.5 h-1.5 rounded-full bg-[#EA580C]" />
              <span className="absolute -inset-1 rounded-full bg-[#EA580C] ping-soft" />
            </span>
            {COMPANY.experienceYears}+ év · Dunaharaszti, M0 mellett
          </motion.div>

          <motion.h1
            data-testid="hero-title"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="mt-6 font-heading font-extrabold text-white text-4xl sm:text-5xl lg:text-7xl leading-[1.02] tracking-tight"
          >
            A jó minőségű ponyva{" "}
            <span className="relative inline-block">
              <span className="text-[#EA580C]">nem szerencse</span>
              <svg
                className="absolute -bottom-1.5 left-0 w-full h-2 md:h-2.5"
                viewBox="0 0 200 12"
                fill="none"
                preserveAspectRatio="none"
              >
                <motion.path
                  d="M2 9 C 60 1, 140 1, 198 9"
                  stroke="#EA580C"
                  strokeWidth="3"
                  strokeLinecap="round"
                  fill="none"
                  initial={{ pathLength: 0 }}
                  animate={{ pathLength: 1 }}
                  transition={{ duration: 1.2, delay: 1 }}
                />
              </svg>
            </span>{" "}
            dolga.
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.3 }}
            data-testid="hero-subtitle"
            className="mt-6 text-base sm:text-lg lg:text-xl text-gray-200 max-w-2xl leading-relaxed"
          >
            Teherautó-, kamion-, utánfutó- és teraszponyva-készítés és -javítás
            kiváló alapanyagokból. Ingyenes állapotfelmérés.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="mt-10 flex flex-col sm:flex-row gap-4"
          >
            <Button
              data-testid="hero-cta-quote"
              onClick={() => scrollTo("ajanlat")}
              className="bg-[#EA580C] hover:bg-[#C2410C] text-white font-semibold h-14 px-8 text-base shadow-xl shadow-orange-900/40 group glow-pulse"
            >
              Ajánlatot kérek
              <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
            </Button>
            <a
              data-testid="hero-cta-phone"
              href={`tel:${COMPANY.phoneRaw}`}
              className="inline-flex items-center justify-center gap-2 h-14 px-8 rounded-md border-2 border-white/40 bg-white/10 hover:bg-white text-white hover:text-[#1E3E62] font-semibold text-base backdrop-blur transition-colors"
            >
              <Phone className="w-5 h-5" />
              {COMPANY.phone}
            </a>
          </motion.div>

          {/* Trust row */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.8 }}
            className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-3 text-white/85 text-sm"
          >
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-0.5">
                {[...Array(5)].map((_, i) => (
                  <Star
                    key={i}
                    className="w-4 h-4 fill-[#EA580C] text-[#EA580C]"
                  />
                ))}
              </div>
              <span>4,5 · Google értékelések</span>
            </div>
            <span className="hidden sm:inline text-white/30">·</span>
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4 text-emerald-400" />
              Munkáinkra garanciát vállalunk
            </div>
            <span className="hidden sm:inline text-white/30">·</span>
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4 text-emerald-400" />
              Minőségi alapanyagok
            </div>
          </motion.div>
        </div>

        {/* Floating right card */}
        <motion.div
          initial={{ opacity: 0, x: 30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.9, delay: 0.6 }}
          className="hidden lg:block absolute right-8 bottom-12 w-72"
        >
          <div className="bg-white/95 backdrop-blur rounded-lg p-5 shadow-2xl border-l-4 border-[#EA580C]">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-md bg-[#EA580C] text-white flex items-center justify-center">
                <ShieldCheck className="w-5 h-5" />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold">
                  Garancia
                </div>
                <div className="font-heading font-bold text-[#0B192C]">
                  Munkáinkra garanciát vállalunk
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Bottom scroll cue */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 hidden md:flex flex-col items-center gap-1.5 text-white/60 text-[10px] uppercase tracking-[0.3em] font-semibold">
        <span>Görgessen</span>
        <span className="block w-px h-8 bg-white/40 relative overflow-hidden">
          <span className="block absolute top-0 left-0 w-full h-1/2 bg-[#EA580C] animate-[count-pop_1.6s_ease-in-out_infinite]" />
        </span>
      </div>
    </section>
  );
};

export default Hero;
